import React from "react";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import { client, mapServerErrors } from "shared/helpers/client";
import useModal from "shared/hooks/useModel";
import { parsePhoneNumber } from "awesome-phonenumber";
import { useCompany } from "shared/context/company";
import { atom, useRecoilState } from "recoil";
import { appendFormData, onlyNumber, passwordPattern, stringify } from "shared/helpers/utils";
import { useLocalize } from "shared/helpers/language";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

interface CompanyInfoData {
  companyName: string;
  employeeName: string;
  employeeEmail: string;
  userName: string;
  employeePhone: string;
  employeeDialCode: string;
  employeePassword:string;
  confirmEmployeePassword:string;
}


interface CompanyLocationData {
  city: string;
  employeeJobDescription: string;
  commercialRegister: string;
  vatNumber: string;
  CommercialRegisterFile: File;
  VatNumberFile: File;
}

type CustomerSignupData = CompanyInfoData & CompanyLocationData;

const customerSignupAtom = atom<Partial<CustomerSignupData>>({
  default:{},
  key:"customer-data"
})

export function useCustomerSignup() {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const [serverErrors,setServerErrors] = React.useState<object>({});
  const [customerSignupData,setCustomerSignupData] = useRecoilState(customerSignupAtom);

  const companySchema = React.useMemo(() =>  yup.object().shape({
    companyName: yup.string().required(t("errors.required")),
    employeeName: yup.string().required(t("errors.required")),
    employeeEmail: yup.string().required(t("errors.required")).email(t('errors.invalid-email')),
    userName: yup.string().required(t("errors.required")).test({
      name: "unique-Name",
      message: t("errors.exist"),
      async test(userName) {
        if(!userName) return  false;
        const { response:isFound } = await client<boolean>(
          "/Owner/VerifyUserName",
          {
            params:{userName : userName},
            method: "POST",
          }
        )
        return isFound || false;
      },
    }),
    employeePhone: yup
      .string()
      .required(t("errors.required"))
      .test({
        name: "phone-validation",
        message: t("errors.invalid-phone"),
        test(phoneNumber) {
          return phoneNumber ? parsePhoneNumber("+" + phoneNumber).isValid() : false;
        },
      }),
    employeePassword: yup.string().required(t("errors.required")).matches(passwordPattern,t('errors.invalid-password')),
    confirmEmployeePassword: yup.string().required(t("errors.required")).equals([yup.ref('employeePassword')], t('errors.password-mismatch'))
  }),[t]);

  const locationSchema = React.useMemo(() =>  yup.object().shape({
    city: yup.string().required(t("errors.required")),
    employeeJobDescription: yup.string().required(t("errors.required")),
    commercialRegister: yup.string().required(t("errors.required")).matches(onlyNumber,t('errors.invalid-field')).test({
      name: "unique-commercial",
      message: t("errors.exist"),
      async test(commercialRegister) {
        if(!commercialRegister) return  false;
        const { response: isFound  } = await client<boolean>(
          "/Owner/VerifyTradeRegister",
          {
            params: { tradeRegistNumber: commercialRegister },
            method: "POST",
          }
        )
        return isFound || false;
      },
    }),
    vatNumber: yup.string().required(t("errors.required")).matches(onlyNumber,t('errors.invalid-field'))
  }),[t]);

  const companyForm = useForm<CompanyInfoData>({
    resolver:yupResolver(companySchema)
  });
  const locationForm = useForm<CompanyLocationData>({
    resolver:yupResolver(locationSchema)
  });

  const { mutate: signup, isLoading } = useMutation((companyDto: any) =>
    client("/Owner/AddNewOwner", { data: companyDto , headers: { "Content-Type": "multipart/form-data" }})
  );

  //const zoneId = locationForm.watch("zone");
  //const {cities,zones,jobDescriptions} = useLookups(['cities','zones','job-description'],zoneId);

  const submit = async (data:CustomerSignupData) => {
    const companyDto = {
      companyName: data.companyName,
      Name: data.employeeName,
      email: data.employeeEmail,
      userName: data.userName,
      JobDescription: data.employeeJobDescription,
      Password:data.employeePassword,
      Phone: data.employeePhone,
      CityName: data.city,
      CommercialRegister:data.commercialRegister,
      VatNumber:data.vatNumber,
      CommercialRegisterFile:data.CommercialRegisterFile,
      VatNumberFile:data.VatNumberFile
      
    };
    const formData = appendFormData(companyDto)

    await signup(formData, {
      onSuccess({ response ,error}) {
        if (response) {
         navigate('/login')
        }
        if (error) {
          setServerErrors(mapServerErrors(error))
        }
      },onError() {
        toast.error(t('errors.server-error'))
      }
    });
  };

  return{
    companyForm,
    locationForm,
    serverErrors,
    customerSignupData,
    setCustomerSignupData,
    submit,
    isLoading
  }

}